import React, { useMemo, useCallback, useState } from 'react';
import { RefreshControl, StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ConfigurationDeleteButton } from '../../components/ConfigurationDeleteButton';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { getOrganizationModuleCapabilities } from '../../config/organizationCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useConfigurationSoftDelete } from '../../hooks/useConfigurationSoftDelete';
import {
  deactivateDepartment,
  getDepartmentDetails,
} from '../../services/departmentService';
import type { DepartmentDetails } from '../../types/department';
import { isActiveConfigurationRecord } from '../../utils/configurationFilters';
import { organizationErrorMessage } from '../../utils/organizationErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'DepartmentDetails'>;

function Field({
  label,
  value,
  palette,
}: {
  label: string;
  value: string | null;
  palette: ColorPalette;
}) {
  return (
    <View style={styles.field}>
      <Text style={[styles.fieldLabel, { color: palette.textSecondary }]}>
        {label}
      </Text>
      <Text style={[styles.fieldValue, { color: palette.textPrimary }]}>
        {value || 'Not listed'}
      </Text>
    </View>
  );
}

export function DepartmentDetailsScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      section: {
        ...typography.sectionTitle,
        color: palette.textPrimary,
        marginBottom: spacing.sm,
        marginTop: spacing.md,
      },
    }),
    [palette],
  );

  const auth = useAuth();
  const capabilities = getOrganizationModuleCapabilities(auth.permissions);
  const { departmentId } = route.params;
  const [item, setItem] = useState<DepartmentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const softDelete = useConfigurationSoftDelete();

  const load = useCallback(
    async (isRefresh = false) => {
      isRefresh ? setRefreshing(true) : setLoading(true);
      try {
        const data = await getDepartmentDetails(departmentId);
        setItem(data);
        setError(null);
      } catch (caught) {
        setItem(null);
        setError(
          organizationErrorMessage(
            caught,
            'The requested record could not be loaded.',
          ),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [departmentId],
  );

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.DEPARTMENT_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !item ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Department"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading department" />
        </AppScreen>
      ) : error && !item ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Department"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={() => load()} />
        </AppScreen>
      ) : item ? (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => load(true)}
            />
          }
        >
          <ScreenHeader
            title={item.departmentName}
            onBack={navigation.goBack}
          />

          <View style={styles.body}>
          <AppCard style={styles.card}>
            {capabilities.canUpdateDepartment &&
            isActiveConfigurationRecord(item.isActive) ? (
              <ConfigurationDeleteButton
                entityLabel="department"
                loading={softDelete.busy}
                disabled={softDelete.busy}
                onPress={() =>
                  softDelete.confirmAndDelete({
                    entityLabel: 'department',
                    recordName: item.departmentName,
                    deleteRecord: async () => {
                      await deactivateDepartment(item);
                    },
                    onSuccess: () => navigation.goBack(),
                    errorMessage: caught =>
                      organizationErrorMessage(
                        caught,
                        'Unable to delete department.',
                      ),
                  })
                }
              />
            ) : null}
            <Field palette={palette} label="Code" value={item.departmentCode} />
            <Field palette={palette} label="Description" value={item.description} />
            <Field palette={palette} label="Location" value={item.locationName} />
            <Field palette={palette} label="Parent department" value={item.parentDepartmentName} />
            <Field palette={palette} label="Status" value={item.statusLabel} />
          </AppCard>
          <View style={styles.actions}>
            {capabilities.canUpdateDepartment ? (
              <AppButton
                title="Edit"
                onPress={() =>
                  navigation.navigate(routes.departmentEdit, { departmentId })
                }
              />
            ) : null}
          </View>
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center' },
  card: { gap: spacing.md },
  field: { gap: spacing.xs },
  fieldLabel: {
    ...typography.caption,
    fontWeight: '600',
  },
  fieldValue: { ...typography.body },
  actions: { gap: spacing.sm, marginTop: spacing.lg },
});
